import React, { useState, useRef, useCallback } from 'react';
import { bool, func, node, number, object, string } from 'prop-types';
import classNames from 'classnames';

import { OutsideClickHandler } from '../../../components';

import FilterForm from '../FilterForm/FilterForm';
import IconPlus from '../IconPlus/IconPlus';

import css from './FilterPopupForSidebar.module.css';

const KEY_CODE_ESCAPE = 27;

function FilterPopupForSidebar({
  rootClassName = null,
  className = null,
  popupClassName = null,
  id,
  onSubmit,
  initialValues = null,
  keepDirtyOnReinitialize = false,
  contentPlacementOffset = 0,
  label = null,
  labelSelection = null,
  isSelected,
  children,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const filterRef = useRef(null);
  const filterContentRef = useRef(null);

  const handleSubmit = useCallback(
    values => {
      setIsOpen(false);
      onSubmit(values);
    },
    [onSubmit]
  );

  const handleClear = useCallback(() => {
    setIsOpen(false);
    onSubmit(null);
  }, [onSubmit]);

  const handleCancel = useCallback(() => {
    setIsOpen(false);
    onSubmit(initialValues);
  }, [onSubmit, initialValues]);

  const handleBlur = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleKeyDown = useCallback(
    e => {
      if (e.keyCode === KEY_CODE_ESCAPE) {
        setIsOpen(false);
      }
    },
    [setIsOpen]
  );

  const toggleOpen = useCallback(
    enforcedState => {
      if (typeof enforcedState === 'boolean') {
        setIsOpen(enforcedState);
      } else {
        setIsOpen(prev => !prev);
      }
    },
    [setIsOpen]
  );

  const positionStyleForContent = useCallback(() => {
    const filter = filterRef.current;
    const filterContent = filterContentRef.current;
    if (filter && filterContent) {
      const distanceToRight = window.innerWidth - filter.getBoundingClientRect().right;
      const labelWidth = filter.offsetWidth;
      const contentWidth = filterContent.offsetWidth;
      const contentWidthBiggerThanLabel = contentWidth - labelWidth;
      const renderToRight = distanceToRight > contentWidthBiggerThanLabel;
      const offset = renderToRight
        ? { left: contentPlacementOffset }
        : { right: contentPlacementOffset };
      const minWidth = contentWidth < labelWidth ? { minWidth: labelWidth } : null;
      return { ...offset, ...minWidth };
    }
    return {};
  }, [contentPlacementOffset]);

  const classes = classNames(rootClassName || css.root, className);
  const popupClasses = classNames(css.popup, { [css.isOpen]: isOpen });
  const popupSizeClasses = popupClassName || css.popupSize;
  const contentStyle = positionStyleForContent();

  return (
    <OutsideClickHandler className={css.root} onOutsideClick={handleBlur}>
      <div className={classes} onKeyDown={handleKeyDown} ref={filterRef}>
        <div className={css.filterHeader}>
          <button type="button" className={css.labelButton} onClick={() => toggleOpen()}>
            <span className={css.labelButtonContent}>
              <span className={css.labelWrapper}>
                <span className={css.label}>
                  {label}
                  {labelSelection ? (
                    <>
                      <span>{': '}</span>
                      <span className={css.labelSelected}>{labelSelection}</span>
                    </>
                  ) : null}
                </span>
              </span>
              <span className={css.openSign}>
                <IconPlus isOpen={isOpen} isSelected={isSelected} />
              </span>
            </span>
          </button>
        </div>
        <div id={id} className={popupClasses} ref={filterContentRef} style={contentStyle}>
          {isOpen ? (
            <FilterForm
              id={`${id}.form`}
              paddingClasses={popupSizeClasses}
              showAsPopup
              contentPlacementOffset={contentPlacementOffset}
              initialValues={initialValues}
              keepDirtyOnReinitialize={keepDirtyOnReinitialize}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              onClear={handleClear}
            >
              {children}
            </FilterForm>
          ) : null}
        </div>
      </div>
    </OutsideClickHandler>
  );
}



FilterPopupForSidebar.propTypes = {
  rootClassName: string,
  className: string,
  popupClassName: string,
  id: string.isRequired,
  onSubmit: func.isRequired,
  initialValues: object,
  keepDirtyOnReinitialize: bool,
  contentPlacementOffset: number,
  label: string.isRequired,
  isSelected: bool.isRequired,
  children: node.isRequired,
};

export default FilterPopupForSidebar;
